/**
 * PLP pins by ad code.
 *
 * Meta ads carry their products in the ad name, which lands in `utm_term` or `utm_content`: a
 * primary product section PP_ and/or a featured products section FP_, e.g.
 *   AF_Video__CN_…__PP_Champ-GK-519__FP_Repea-AK-735|Tiger-KK-567__SN_…__ID_…
 * The primary product goes first, then the featured ones in their order (max 8 in total).
 * Every paper print carries the tag `adcode:<code>` (the Airtable `Meta Ad Name ID`), so each code
 * resolves with one small request: /collections/prints/<tag handle>?view=wd-adcode.
 * Resolved products are shown first in the theme's collection grid (main-collection →
 * snippets/product-grid.liquid), in the ad's order, right after the first tile. A product whose
 * card is on page 1 is moved; one that is not gets the theme's own card for it, rendered by the
 * same section for just that product, and its real card is hidden when infinite scroll brings it.
 *
 * Rules (agreed with Margherita, 2026-09-25 → 2026-10-20):
 * - No PP_ or FP_ section in the URL: nothing runs, no request, no DOM change.
 * - Pins live at the top of page 1 only. A page that loads at ?page=N (back from a product) pins
 *   nothing until infinite scroll brings page 1 back in above.
 * - While a filter is active (tags in the path, or filter.* parameters): only pinned products on
 *   page 1 of the results go first; nothing is added. With no filter (on landing, or after
 *   clearing them all) cards are added as needed.
 * - While the sort differs from the one the page landed with: no pins. Back to that sort (e.g.
 *   Featured), the pins come back (Margherita, 2026-10-20; until then a sort change dropped them
 *   until reload).
 * - Fail open: any error leaves the normal page.
 *
 * The grid tells us when it changes: `wd:grid-update` on document, fired by paginated-list.js
 * after infinite scroll adds a page and by wd-collection-filters.js after a filter, sort or
 * back/forward re-render. Every pass is idempotent: when the grid is already in pinned order it
 * touches nothing, so drool-edit-spots.js (which re-places its spots when products move) settles
 * after one round.
 */
(function () {
  'use strict';

  if (window.droolPlpPinLoaded) return;
  window.droolPlpPinLoaded = true;

  var MAX_CODES = 8;
  var FETCH_MS = 8000;
  var GRID = '.main-collection-grid ul.product-grid';
  var GRID_EVENT = 'wd:grid-update';
  var FILTER_EVENT = 'filter:update';
  var TAG_PREFIX = 'adcode:';

  /* ---------------------------------------------------------------- codes from the URL */

  // The product codes in an ad name: sections are separated by `__`, codes inside a section by
  // `|`. PP_ (primary product) comes first, then FP_ (featured products); either may be missing.
  // A code in both counts once, in its first place.
  var CODE_SECTIONS = ['PP_', 'FP_'];

  function adCodes(value) {
    var parts = String(value || '').split('__');
    var codes = [];
    CODE_SECTIONS.forEach(function (prefix) {
      parts.forEach(function (part) {
        if (part.indexOf(prefix) !== 0) return;
        part.slice(prefix.length).split('|').forEach(function (code) {
          code = code.trim();
          if (code && codes.indexOf(code) === -1) codes.push(code);
        });
      });
    });
    return codes;
  }

  function readCodes(params) {
    var codes = adCodes(params.get('utm_content'));
    // Safety net: an ad name that arrived in another parameter (seen in utm_term).
    if (!codes.length) {
      params.forEach(function (value, key) {
        if (codes.length || key === 'utm_content') return;
        if (value.indexOf('__PP_') !== -1 || value.indexOf('__FP_') !== -1) codes = adCodes(value);
      });
    }
    // The cap counts PP and FP together; PP is first, so the primary product is never cut.
    return codes.slice(0, MAX_CODES);
  }

  /* ---------------------------------------------------------------- resolving codes */

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.replace(/\/?$/, '/');
  }

  // Shopify's tag URL for `adcode:<code>`. Verified on the live store (2026-10-07) for every
  // character class in the code list: lower-case, keep letters, digits, accents, CJK and most
  // punctuation as they are, but a dot 404s and a `+` means "AND another tag", so spaces, `:`,
  // `.` and `+` become `-`.
  function tagHandle(code) {
    return (TAG_PREFIX + code).toLowerCase().replace(/[\s:.+]+/g, '-');
  }

  // Shopify's classic handle form, tried only when the first form 404s.
  function tagHandleStrict(code) {
    return (TAG_PREFIX + code).toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '');
  }

  // `as` is 'json' or 'text'. Resolves 404 for a 404, so callers can try another URL.
  function request(url, as) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_MS) : 0;
    return fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (response.status === 404) return 404;
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response[as]();
      })
      .finally(function () { clearTimeout(timer); });
  }

  function fetchJson(url) {
    return request(url, 'json');
  }

  // templates/collection.wd-adcode.liquid: the tag echo plus at most two products, and no product
  // loop at all when the tag matches nothing, so a mistyped code answers as fast as a real one.
  function lookupUrl(handle) {
    return root() + 'collections/prints/' + encodeURIComponent(handle) + '?view=wd-adcode';
  }

  // One code → { print, tag } (the paper print and the tag handle that found it), or null. The
  // answer counts only when Shopify echoes the tag: for a tag no product carries it drops the
  // filter and returns the whole collection.
  function resolveCode(code) {
    // A Shopify tag cannot contain a comma, so such a code can never match.
    if (code.indexOf(',') !== -1) return Promise.resolve(null);
    var wanted = (TAG_PREFIX + code).toLowerCase();
    var tag = tagHandle(code);
    return fetchJson(lookupUrl(tag))
      .then(function (data) {
        if (data !== 404) return data;
        tag = tagHandleStrict(code);
        return fetchJson(lookupUrl(tag));
      })
      .then(function (data) {
        if (!data || data === 404 || !Array.isArray(data.currentTags)) return null;
        var echoed = data.currentTags.some(function (t) { return String(t).toLowerCase() === wanted; });
        if (!echoed) return null;
        var prints = (data.products || []).filter(function (p) { return p && p.type === 'Print'; });
        return prints.length === 1 ? { print: prints[0], tag: tag } : null;
      })
      .catch(function () { return null; });
  }

  function fetchProduct(handle) {
    return fetchJson(root() + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (data) { return data && data !== 404 ? data : null; })
      .catch(function () { return null; });
  }

  /* ---------------------------------------------------------------- grid helpers */

  function gridEl() {
    return document.querySelector(GRID);
  }

  function sectionId() {
    var list = document.querySelector('results-list[section-id]');
    return list ? list.getAttribute('section-id') : '';
  }

  // Product cards, ours included. The reserved first cell and content spot cells carry no
  // data-product-id (drool-edit-spots.js relies on the same rule).
  function isProductCell(node) {
    return node.nodeType === 1 && node.hasAttribute('data-product-id');
  }

  function isInjected(node) {
    return node.classList.contains('wd-plp-pin--injected');
  }

  function cardHandle(card) {
    var link = card.querySelector('a[ref="productCardLink"]') || card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    try {
      var match = new URL(link.getAttribute('href'), window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function isDuplicate(card) {
    return card.classList.contains('wd-plp-pin--duplicate');
  }

  function hideDuplicate(card) {
    if (isDuplicate(card)) return;
    card.classList.add('wd-plp-pin--duplicate');
    card.style.display = 'none';
    card.setAttribute('aria-hidden', 'true');
  }

  function showDuplicate(card) {
    if (!isDuplicate(card)) return;
    card.classList.remove('wd-plp-pin--duplicate');
    card.style.display = '';
    card.removeAttribute('aria-hidden');
  }

  // /collections/<handle>[/<tag+tag>], after an optional locale prefix (/en-us/collections/…).
  function collectionPath() {
    var match = window.location.pathname.match(/\/collections\/([^/]+)(?:\/([^/]+))?/);
    return match ? { handle: match[1], tags: match[2] || '' } : null;
  }

  /* ---------------------------------------------------------------- the theme's card */

  // The card for `handle` in a section render, or null. Only a render of this same section (same
  // section id) has the same card markup as the grid.
  function cardFrom(html, sid, handle) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var list = doc.querySelector('results-list[section-id]');
    if (!list || list.getAttribute('section-id') !== sid) return null;
    var cards = list.querySelectorAll('ul.product-grid > li[data-product-id]');
    for (var i = 0; i < cards.length; i++) {
      if (cardHandle(cards[i]) === handle) return cards[i];
    }
    return null;
  }

  // The same card as the one infinite scroll will add later, so it needs its own ids: the section
  // morph keys on id, and two equal ids would make it match the wrong node. Inner ids get the
  // product id too: every card rendered on its own is card #0, so their inner ids are all equal.
  function prepareCard(card, handle) {
    var productId = card.getAttribute('data-product-id');
    var suffix = '-wd-pin-' + productId;
    card.id = 'wd-plp-pin-' + productId;
    Array.prototype.forEach.call(card.querySelectorAll('[id]'), function (n) { n.id = n.id + suffix; });
    card.classList.add('wd-plp-pin--injected');
    card.setAttribute('data-wd-pinned', handle);
    // Pins sit on page 1. paginated-list.js reads data-page from the first and last cards.
    card.setAttribute('data-page', '1');
    // At the top of the page: load the image now, not when lazysizes gets to it.
    var img = card.querySelector('img');
    if (img) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
    }
    return card;
  }

  // Where the theme renders this pin's card on its own. Paper prints: the tag path on this
  // collection, then on /collections/prints (a per-ad collection may not hold the product).
  // Canvas twins carry no tag: the canvas collection filtered to the artist, which holds the
  // card unless the artist has more than one page of canvases.
  function tagCardUrl(collection, tag, sid) {
    return root() + 'collections/' + collection + '/' + encodeURIComponent(tag) + '?section_id=' + encodeURIComponent(sid);
  }

  function cardUrls(pin, path, sid) {
    if (canvas) {
      return [root() + 'collections/' + path.handle + '?filter.p.vendor=' + encodeURIComponent(pin.vendor) +
        '&section_id=' + encodeURIComponent(sid)];
    }
    var urls = [tagCardUrl(path.handle, pin.tag, sid)];
    if (path.handle !== 'prints') urls.push(tagCardUrl('prints', pin.tag, sid));
    return urls;
  }

  // Resolves once pin.card is known: the prepared card, or null when there is none.
  function loadCard(pin) {
    if (pin.cardPromise) return pin.cardPromise;
    var sid = sectionId();
    var path = collectionPath();
    if (!sid || !path) {
      pin.card = null;
      pin.cardPromise = Promise.resolve();
      return pin.cardPromise;
    }
    var urls = cardUrls(pin, path, sid);
    var attempt = function (i) {
      if (i >= urls.length) return null;
      return request(urls[i], 'text').then(function (html) {
        var card = html === 404 ? null : cardFrom(html, sid, pin.handle);
        return card || attempt(i + 1);
      });
    };
    // The first URL may already be on its way (earlyCard): use that answer when it is for this tag.
    var first = Promise.resolve(pin.early).then(function (early) {
      if (!early || early.tag !== pin.tag || early.sid !== sid) return attempt(0);
      return cardFrom(early.html, sid, pin.handle) || attempt(1);
    });
    pin.cardPromise = first
      .then(function (card) {
        pin.card = card ? prepareCard(card, pin.handle) : null;
        // A canvas twin of an artist with more than one page of canvases is a known gap, not an error.
        if (!card && !canvas) window.droolReportError && window.droolReportError('wd-plp-pin', 'no card for ' + pin.handle);
      })
      .catch(function (error) {
        pin.card = null;
        window.droolReportError && window.droolReportError('wd-plp-pin', 'card ' + pin.handle + ': ' + String(error));
      });
    return pin.cardPromise;
  }

  // Inserting above the viewport (page 1 loaded back in above a restored ?page=N) would push the
  // shopper's view down. Keep the first visible cell where it was, like paginated-list.js does
  // for the page it prepends.
  function keepingScroll(list, change) {
    var anchor = null;
    var before = 0;
    if (list.getBoundingClientRect().top < 0) {
      for (var i = 0; i < list.children.length; i++) {
        var rect = list.children[i].getBoundingClientRect();
        if (rect.height && rect.bottom > 0) {
          anchor = list.children[i];
          before = rect.top;
          break;
        }
      }
    }
    change();
    if (!anchor || !anchor.isConnected) return;
    var diff = anchor.getBoundingClientRect().top - before;
    if (diff) window.scrollTo({ top: window.scrollY + diff, behavior: 'instant' });
  }

  /* ---------------------------------------------------------------- state */

  var params = new URLSearchParams(window.location.search);
  var codes = readCodes(params);
  if (!codes.length) return;

  var landingSort = params.get('sort_by') || '';
  // Same rule as window.FrameType in snippets/scripts.liquid, read here directly: this script is
  // async and can run before that inline script.
  var canvas = window.location.pathname.indexOf('canvas') !== -1;
  var pins = null;          // [{ code, handle, tag, vendor, card }] once resolved
  var unresolved = [];
  var filtered = false;     // a filter is active now: pins only reorder, nothing is added
  var sorted = false;       // the sort differs from the landing sort now: no pins
  var dropped = false;      // sticky: after an error, pins are gone until reload (fail open)
  var reported = false;
  var scheduled = false;
  var stats = { injected: 0, reordered: 0 };

  function report() {
    if (reported) return;
    reported = true;
    // A pin that needed an added card and got none (pin.card === null) was not shown: unresolved
    // (Margherita, 2026-10-20). Undefined = no card was needed.
    var shown = (pins || []).filter(function (p) { return p.card !== null; });
    var noCard = (pins || []).filter(function (p) { return p.card === null; });
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'PLP pin',
        plp_pin_codes: codes.slice(),
        plp_pin_resolved: shown.map(function (p) { return p.handle; }),
        plp_pin_unresolved: unresolved.concat(noCard.map(function (p) { return p.code; })),
        plp_pin_injected: stats.injected,
        plp_pin_reordered: stats.reordered
      });
    } catch (error) {
      /* analytics must never break the page */
    }
  }

  // Filters are read as they are now, not compared with the landing URL (Margherita, 2026-10-08):
  // a page that loads with a filter (back from a product page, a reload) is filtered, and once
  // every filter is cleared the page is the ad landing again, added cards included. Filters are
  // tags in the path (/collections/prints/colour2026aug-blue) or filter.* parameters.
  function readUrlState() {
    var current = new URLSearchParams(window.location.search);
    sorted = (current.get('sort_by') || '') !== landingSort;
    var path = collectionPath();
    var filterParam = false;
    current.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) filterParam = true;
    });
    // The collection shortlist test's tag (snippets/wd-short-ab-prepaint.liquid) is not a
    // shopper's filter: a shortlist landing still gets its pins, added cards included.
    var shortTag = window.droolPlpArm ? window.droolPlpArm.tag : '';
    var pathTags = path && path.tags ? path.tags.split('+').filter(function (tag) {
      return tag && tag.toLowerCase() !== shortTag;
    }) : [];
    filtered = pathTags.length > 0 || filterParam;
  }

  function unpin(list) {
    Array.prototype.forEach.call(list.querySelectorAll('.wd-plp-pin--injected'), function (n) { n.remove(); });
    Array.prototype.forEach.call(list.querySelectorAll('.wd-plp-pin--duplicate'), showDuplicate);
  }

  /* ---------------------------------------------------------------- one pass */

  function pass() {
    scheduled = false;
    if (!pins) return;
    var list = gridEl();
    if (!list) return;

    readUrlState();
    if (sorted) {
      unpin(list);
      report();
      return;
    }

    var cells = Array.prototype.filter.call(list.children, isProductCell);
    var real = cells.filter(function (c) { return !isInjected(c); });
    if (!real.length) {
      report();   // empty results: nothing to pin into
      return;
    }
    // Pins live at the top of page 1. Landed on a later page: wait for page 1 to load in above.
    if (real[0].getAttribute('data-page') !== '1') {
      report();
      return;
    }
    var realHandles = real.map(cardHandle);

    var desired = [];
    var missing = [];
    pins.forEach(function (pin) {
      var matches = real.filter(function (c, i) { return realHandles[i] === pin.handle; });
      // Only a card from page 1 moves. A card from a later page at the very top would make
      // paginated-list.js think the previous page is missing.
      var onPage1 = matches.filter(function (c) { return c.getAttribute('data-page') === '1'; })[0];
      var injected = list.querySelector('.wd-plp-pin--injected[data-wd-pinned="' + CSS.escape(pin.handle) + '"]');

      // Filtered: only what the theme rendered goes first. Unfiltered: the theme's card while
      // there is no added card yet; once one exists it stays, and the real card is hidden when
      // infinite scroll brings it.
      if (filtered || (!injected && onPage1)) {
        if (injected) injected.remove();
        matches.forEach(showDuplicate);
        if (onPage1) desired.push(onPage1);
        return;
      }

      if (!injected) {
        if (pin.card === undefined) {
          missing.push(pin);
          return;
        }
        if (!pin.card) return;   // no card to add
        injected = pin.card.cloneNode(true);
      }
      matches.forEach(hideDuplicate);
      desired.push(injected);
    });

    // Some cards still loading: apply everything in one go once they are here (one visible change).
    if (missing.length) {
      Promise.all(missing.map(loadCard)).then(schedule);
      return;
    }

    // Already in pinned order? Then touch nothing (keeps every observer on the page quiet).
    var visible = Array.prototype.filter.call(list.children, function (c) {
      return isProductCell(c) && !isDuplicate(c);
    });
    var inOrder = desired.every(function (card, i) { return visible[i] === card; });
    if (!inOrder) {
      // After the first tile (reserved cell, position-0 spot): before the first product that is
      // not pinned.
      var ref = null;
      for (var i = 0; i < list.children.length; i++) {
        var child = list.children[i];
        if (isProductCell(child) && !isDuplicate(child) && desired.indexOf(child) === -1) { ref = child; break; }
      }
      keepingScroll(list, function () {
        desired.forEach(function (card) {
          if (ref) list.insertBefore(card, ref);
          else list.appendChild(card);
        });
      });
      desired.forEach(function (card) {
        var img = isInjected(card) && card.querySelector('img.lazyload');
        if (img && window.lazySizes && window.lazySizes.loader) window.lazySizes.loader.unveil(img);
      });
      // Cards were added: tell the grid's other scripts (wd-collection-frames.js). watch() ignores
      // this reason, so it does not start another pass.
      if (desired.some(isInjected)) {
        document.dispatchEvent(new CustomEvent(GRID_EVENT, { detail: { reason: 'pin' } }));
      }
    }

    if (!reported) {
      stats.injected = list.querySelectorAll('.wd-plp-pin--injected').length;
      stats.reordered = desired.length - stats.injected;
      report();
    }
  }

  function run() {
    try {
      pass();
    } catch (error) {
      // Fail open: leave the theme's grid and stop pinning.
      dropped = true;
      try {
        var list = gridEl();
        if (list) unpin(list);
      } catch (ignore) {
        /* nothing more to undo */
      }
      report();
      window.droolReportError && window.droolReportError('wd-plp-pin', String(error));
    }
  }

  // Before the next paint, so the shopper never sees a page's duplicate card or the grid out of
  // order. A page that gets no animation frames (background tab, or a webview that reports
  // "visible" but does not paint) runs on the timer instead: nothing paints there anyway.
  function schedule() {
    if (scheduled || dropped) return;
    scheduled = true;
    var done = false;
    var once = function () {
      if (done) return;
      done = true;
      run();
    };
    if (document.visibilityState !== 'hidden') window.requestAnimationFrame(once);
    setTimeout(once, document.visibilityState === 'hidden' ? 0 : 100);
  }

  function watch() {
    document.addEventListener(GRID_EVENT, function (event) {
      if (event.detail && event.detail.reason === 'pin') return;
      schedule();
    });
    // Fired before the re-render, with the new URL already pushed. A sort change takes the pins
    // off now; everything else waits for the re-rendered grid (GRID_EVENT).
    document.addEventListener(FILTER_EVENT, function () {
      readUrlState();
      if (sorted) run();
    });
  }

  /* ---------------------------------------------------------------- start */

  // Resolves once test() passes or the document is parsed. This script is async and runs in
  // <head> (snippets/scripts.liquid), and nothing signals when the parser reaches an element, so
  // an observer watches the document while it loads and stops at the first pass or at
  // DOMContentLoaded. Not a timer: timers are slowed in background pages and wait behind long
  // tasks, which is the case this is for.
  function whenParsed(test) {
    return new Promise(function (resolve) {
      if (test() || document.readyState !== 'loading' || typeof MutationObserver !== 'function') return resolve();
      var observer = new MutationObserver(function () {
        if (test()) done();
      });
      var done = function () {
        observer.disconnect();
        document.removeEventListener('DOMContentLoaded', done);
        resolve();
      };
      observer.observe(document.documentElement, { childList: true, subtree: true });
      document.addEventListener('DOMContentLoaded', done);
    });
  }

  // The main section id, needed for the card requests.
  var sectionReady = whenParsed(function () { return !!sectionId(); }).then(sectionId);

  // The grid is complete once the infinite-scroll marker that follows the product list
  // (snippets/product-grid.liquid) is parsed. Not DOMContentLoaded, which also waits for every
  // deferred theme script to run: slow on phones and in in-app browsers.
  var gridReady = whenParsed(function () {
    return !!document.querySelector('.main-collection-grid [ref="viewMoreNext"]');
  });

  // A code → its product for this collection: the paper print, or its -canvas twin on canvas
  // collections, which is only known from its own product.
  function lookup(code) {
    return resolveCode(code).then(function (found) {
      if (!found) return null;
      var pin = { code: code, handle: found.print.handle, tag: found.tag, available: found.print.available };
      if (!canvas) return pin;
      pin.handle += '-canvas';
      return fetchProduct(pin.handle).then(function (product) {
        if (!product) return null;
        pin.vendor = product.vendor;
        pin.available = product.available;
        return pin;
      });
    });
  }

  // Whether the first pass will add a card for this pin: unfiltered landing sort, page 1 on the
  // page, and no real card for it on page 1. Same rules as pass().
  function needsCard(pin) {
    readUrlState();
    if (filtered || sorted) return false;
    var list = gridEl();
    if (!list) return false;
    var real = Array.prototype.filter.call(list.children, function (c) { return isProductCell(c) && !isInjected(c); });
    if (!real.length || real[0].getAttribute('data-page') !== '1') return false;
    return !real.some(function (c) { return c.getAttribute('data-page') === '1' && cardHandle(c) === pin.handle; });
  }

  // The card request for a code, started together with its lookup instead of after it: the tag
  // path is known from the code alone (the lookup then decides whether the answer is used). Saves
  // one round trip, which is what slow in-app browsers feel. Skipped where no card is added on
  // landing (a filter or ?page=N in the URL) and on canvas collections (the card needs the
  // artist, known only after the lookup). A card for a product already on page 1 is downloaded
  // for nothing (about 12 KB); a wrong code downloads a full page of cards.
  function earlyCard(code) {
    if (canvas || code.indexOf(',') !== -1) return null;
    readUrlState();
    if (filtered || (parseInt(params.get('page'), 10) || 1) > 1) return null;
    var tag = tagHandle(code);
    return sectionReady.then(function (sid) {
      var path = collectionPath();
      if (!sid || !path) return null;
      return request(tagCardUrl(path.handle, tag, sid), 'text').then(function (html) {
        return html === 404 ? null : { tag: tag, sid: sid, html: html };
      });
    }).catch(function () { return null; });
  }

  // Each code's card is used as soon as its own lookup is back, not after all of them.
  function lookupAndPrefetch(code) {
    var early = earlyCard(code);
    return Promise.all([lookup(code), gridReady]).then(function (results) {
      var pin = results[0];
      if (!pin) return pin;
      pin.early = early;
      if (pin.available && needsCard(pin)) loadCard(pin);
      return pin;
    });
  }

  function start() {
    Promise.all(codes.map(lookupAndPrefetch))
      .then(function (results) {
        var seen = {};
        pins = [];
        codes.forEach(function (code, i) {
          var pin = results[i];
          // Skipped: no match, the same artwork twice, or not for sale (DROOL prints on demand,
          // so this is only a safety net).
          if (!pin || seen[pin.handle] || !pin.available) {
            unresolved.push(code);
            return;
          }
          seen[pin.handle] = true;
          pins.push(pin);
        });
        if (!pins.length) {
          report();
          return;
        }
        watch();
        schedule();
      })
      .catch(function (error) {
        pins = pins || [];
        report();
        window.droolReportError && window.droolReportError('wd-plp-pin', String(error));
      });
  }

  start();
})();
